/* eslint-disable @atlassian/relay/unused-fields */
import type { DataTag } from '@tanstack/query-core';
import {
	infiniteQueryOptions,
	queryOptions,
	type InfiniteData,
	type OmitKeyof,
	type QueryFunction,
	type UseInfiniteQueryOptions,
	type useQueryClient,
	type UseQueryOptions,
} from '@tanstack/react-query';
import { createResource, type RouteResource } from 'react-resource-router-next';
import { fetchQuery, graphql, type IEnvironment } from 'relay-runtime';

import { fg } from '@atlaskit/platform-feature-flags';
import { logException } from '@atlassian/atlassian-studio-observability';

import type {
	AssignmentDeletionRequest,
	AssignmentSubmissionRequest,
	RoleAssignmentRequest,
} from '../common/types/assignments';
import type { Group, GroupSearchRequest } from '../common/types/groups';
import type { AvailableWorkspacesResponse, OrgContextApi } from '../common/types/org-context';
import type {
	AssignmentResponse,
	AssignmentRoleResponse,
	PermissionsResponse,
} from '../common/types/permissions';
import type { SpaceResponse, SpacesListResponse } from '../common/types/spaces';

import type { services_RecentActivityForWorkspaceSelectionQuery } from './__generated__/services_RecentActivityForWorkspaceSelectionQuery.graphql';

declare global {
	interface Window {
		STUDIO_APP_VERSION: string;
	}
}

const fetchOrgContextData = async (workspaceId: string): Promise<OrgContextApi> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/context`,
	);
	if (!response.ok) {
		throw new Error('Failed to fetch studio workspace context data');
	}
	return response.json();
};

export const orgContextQueryOptions = (
	workspaceId: string,
): OmitKeyof<
	UseQueryOptions<OrgContextApi, Error, OrgContextApi, (string | undefined)[]>,
	'queryFn'
> & {
	queryFn?: QueryFunction<OrgContextApi, (string | undefined)[], never> | undefined;
} & {
	queryKey: DataTag<(string | undefined)[], OrgContextApi, Error>;
} =>
	queryOptions({
		queryKey: getOrgContextQueryKey(workspaceId),
		queryFn: () => fetchOrgContextData(workspaceId),
		enabled: !!workspaceId,
		staleTime: Infinity,
	});

export const fetchAvailableUnitAwareWorkspaces = async (): Promise<AvailableWorkspacesResponse> => {
	const response = await fetch('/gateway/studio/api/studio/collaborationcontext/workspace', {
		// @NOTE: This is a POST, because the API still supports the ability to supply a
		// list of CC's/Units in the request body. For now, we do not need this functionality though.
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
	});
	if (!response.ok) {
		throw new Error('Failed to fetch available workspaces');
	}
	return response.json();
};

const getOrgContextQueryKey = (workspaceId?: string): (string | undefined)[] => [
	'org-context',
	workspaceId,
];
export const getAvailableUnitAwareWorkspacesQueryKey = (): string[] => {
	return ['studio-BE-available-unit-aware-workspaces'];
};
export const getWorkspaceIdBySiteIdQueryKey = (siteId: string): string[] => [
	'workspace-id-by-site-id',
	siteId,
];
export const getRecentWorkspaceForUserQueryKey = (atlassianAccountId: string): string[] => [
	'recent-workspace-for-user',
	atlassianAccountId,
];

const fetchSpaceBySpaceId = async (
	workspaceId: string,
	spaceId: string,
): Promise<SpaceResponse> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/spaces/${spaceId}`,
	);
	if (!response.ok) {
		throw new Error('Failed to fetch space by space id');
	}
	return response.json();
};

const getSpaceQueryKey = (workspaceId: string, spaceId: string): string[] => [
	'space',
	workspaceId,
	spaceId,
];
export const getSpaceQueryOptions = (
	workspaceId: string,
	spaceId: string,
): OmitKeyof<UseQueryOptions<SpaceResponse, Error, SpaceResponse, string[]>, 'queryFn'> & {
	queryFn?: QueryFunction<SpaceResponse, string[], never> | undefined;
} & {
	queryKey: DataTag<string[], SpaceResponse, Error>;
} =>
	queryOptions({
		queryKey: getSpaceQueryKey(workspaceId, spaceId),
		queryFn: () => fetchSpaceBySpaceId(workspaceId, spaceId),
		enabled: Boolean(workspaceId) && Boolean(spaceId),
		staleTime: Infinity,
	});

const fetchSpacesApi = async (
	workspaceId: string,
	opts: { after?: string | null; query?: string },
	errorMessage: string,
): Promise<SpacesListResponse> => {
	const params = new URLSearchParams();
	if (opts?.query) {
		params.set('query', opts.query);
	}
	if (opts?.after) {
		params.set('after', opts.after);
	}

	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/spaces?${params.toString()}`,
	);

	if (!response.ok) {
		throw new Error(errorMessage);
	}
	return response.json();
};

type SpacesPageParam = {
	cursor: string | null;
	search?: string;
};

export const getSpacesInfiniteQueryKey = (workspaceId: string, search?: string): string[] => [
	'spaces-infinite',
	workspaceId,
	search || '',
];

export const getSpacesInfiniteQueryOptions = (
	workspaceId: string,
	search?: string,
): OmitKeyof<
	UseInfiniteQueryOptions<
		SpacesListResponse,
		Error,
		InfiniteData<SpacesListResponse, unknown>,
		string[],
		SpacesPageParam | null
	>,
	'queryFn'
> & {
	queryFn?: QueryFunction<SpacesListResponse, string[], SpacesPageParam | null> | undefined;
} & {
	queryKey: DataTag<string[], InfiniteData<SpacesListResponse, unknown>, Error>;
} =>
	infiniteQueryOptions({
		queryKey: getSpacesInfiniteQueryKey(workspaceId, search),
		queryFn: ({ pageParam }) =>
			fetchSpacesApi(
				workspaceId,
				{
					query: search,
					after: pageParam?.cursor ?? null,
				},
				'Failed to fetch spaces',
			),
		initialPageParam: null,
		getNextPageParam: (lastPage: SpacesListResponse) => {
			if (!lastPage.hasNext || !lastPage.nextCursor) {
				return null;
			}

			return {
				cursor: lastPage.nextCursor,
				search,
			} as SpacesPageParam;
		},
		staleTime: Infinity,
	});

export const createSpace = async (
	workspaceId: string,
	data: { name: string },
): Promise<SpaceResponse> => {
	const response = await fetch(`/gateway/studio/workspace/${workspaceId}/api/studio/spaces`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	});
	if (!response.ok) {
		throw new Error(`Failed to create space: ${response.status} ${response.statusText}`);
	}
	return response.json();
};

export class WorkspaceBySiteIdError extends Error {
	constructor(readonly status: number) {
		super('Failed to fetch workspace ID by site ID');
	}
}

export const isForbiddenError = (error: unknown): boolean =>
	error instanceof WorkspaceBySiteIdError && error.status === 403;

export const getWorkspaceIdBySiteId = async (
	siteId: string,
): Promise<{ id: string | undefined }> => {
	const response = await fetch(`/gateway/studio/api/studio/site/${siteId}/workspace`);
	if (!response.ok && response.status !== 404) {
		const error = new WorkspaceBySiteIdError(response.status);
		logException(error, {
			loggerName: 'studio-workspace-context',
			teamName: 'studio',
		});
		throw error;
	}

	// 404 is returned when there are no v2 workspaces for the site
	if (response.status === 404) {
		logException(new Error('Could not find workspace ID for site ID'), {
			loggerName: 'studio-workspace-context',
			teamName: 'studio',
		});
		return { id: undefined };
	}

	return response.json();
};

export const getRecentWorkspaceForUser = async (
	environment: IEnvironment,
	queryClient: ReturnType<typeof useQueryClient>,
): Promise<string | undefined> => {
	const data = await fetchQuery<services_RecentActivityForWorkspaceSelectionQuery>(
		environment,
		graphql`
			query services_RecentActivityForWorkspaceSelectionQuery {
				activity {
					myActivity {
						all(
							filter: {
								arguments: {
									products: ["studio"]
									objectTypes: ["workspace"]
									eventTypes: ["viewed"]
								}
							}
							first: 1
						) {
							edges {
								node {
									object {
										id
										type
										product
										rootContainerId
									}
									event {
										eventType
										timestamp
									}
								}
							}
						}
					}
				}
			}
		`,
		{},
	).toPromise();

	// @NOTE: ariId formatting is something like: ari:cloud:studio::workspace/7d965d21-0e28-43d6-90f1-60b0df1431a0
	const ariId = data?.activity?.myActivity?.all?.edges?.[0]?.node?.object?.id;

	if (!ariId) {
		return undefined;
	}

	// If there is no slash in the ariId, we probably wont recognise it's format,
	// so we should not use it, and simply return undefined
	const parsedWorkspaceId = ariId?.includes('/') ? ariId.split('/').pop() : ariId;

	if (!parsedWorkspaceId) {
		return undefined;
	}
	const allWorkspacesResponse = await queryClient.fetchQuery({
		queryKey: getAvailableUnitAwareWorkspacesQueryKey(),
		queryFn: () => fetchAvailableUnitAwareWorkspaces(),
		staleTime: Infinity,
	});
	const allAvailableWorkspaces = Object.values(allWorkspacesResponse).filter(Boolean);
	const isRecentWorkspaceAvailable =
		Object.values(allAvailableWorkspaces).includes(parsedWorkspaceId);
	return isRecentWorkspaceAvailable ? parsedWorkspaceId : undefined;
};

export const permissionResponse: RouteResource<PermissionsResponse> =
	createResource<PermissionsResponse>({
		type: 'PERMISSION',
		getKey: (routerContext) => {
			const workspaceId = routerContext.match.params.workspaceId;
			const { searchQuery, roleIds } = routerContext.query;
			return `permissions-${workspaceId}-${searchQuery || ''}-${roleIds || ''}`;
		},
		getData: async (routerContext): Promise<PermissionsResponse> => {
			const workspaceId = routerContext.match.params.workspaceId;
			if (!workspaceId) {
				return {
					assignments: [],
					roles: [],
				};
			}

			const url = new URL(
				`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/permissions/roles`,
				window.location.origin,
			);

			if (fg('studio-settings-support-search')) {
				const { searchQuery, roleIds } = routerContext.query;
				if (searchQuery) {
					url.searchParams.set('query', searchQuery);
				}
				if (roleIds) {
					url.searchParams.set('roleIds', roleIds);
				}
			}

			const permissionsResponse = await fetch(url.toString());
			if (!permissionsResponse.ok) {
				throw new Error('Failed to fetch permissions');
			}
			const responseBody = await permissionsResponse.json();
			const assignments: AssignmentResponse[] = responseBody?.assignments ?? [];

			return {
				assignments,
				roles:
					// Filter out AGENT_CREATOR roles for now as they are not supported yet
					responseBody?.roles?.filter(
						(role: AssignmentRoleResponse) => role.roleType !== 'AGENT_CREATOR',
					) ?? [],
			};
		},
		isBrowserOnly: true,
	});

export const searchGroups = async (
	workspaceId: string,
	request: GroupSearchRequest,
): Promise<Group[]> => {
	// New GET endpoint (no CSRF) used by the AddPrincipalModal search flow
	const params = new URLSearchParams();
	if (request.nameContains) {
		params.set('name', request.nameContains);
	}
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/group/search?${params.toString()}`,
	);
	if (!response.ok) {
		throw new Error('Failed to search groups');
	}
	return response.json();
};

export const searchWorkspaceUsers = async (
	workspaceId: string,
	name?: string,
): Promise<
	{
		accountId: string;
		displayName: string;
		emailAddress: string | null;
		picture: string | null;
	}[]
> => {
	const params = new URLSearchParams({
		requireStudioAccess: 'false',
		accountTypes: 'atlassian',
	});
	if (name) {
		params.set('name', name);
	}
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/user/search?${params.toString()}`,
	);
	if (!response.ok) {
		return [];
	}
	return response.json();
};

const getGroupsQueryKey = (workspaceId: string, nameContains?: string): string[] => [
	'groups-search',
	workspaceId,
	nameContains || '',
];

export const getGroupsQueryOptions = (
	workspaceId: string,
	nameContains: string = '',
): OmitKeyof<UseQueryOptions<Group[], Error, Group[], string[]>, 'queryFn'> & {
	queryFn?: QueryFunction<Group[], string[], never> | undefined;
} & {
	queryKey: DataTag<string[], Group[], Error>;
} =>
	queryOptions({
		queryKey: getGroupsQueryKey(workspaceId, nameContains),
		queryFn: () => searchGroups(workspaceId, { nameContains }),
		enabled: Boolean(workspaceId),
		staleTime: Infinity,
	});

/**
 * Submits principal role assignments to the workspace permissions API.
 * POST /api/studio/workspace/permissions/roles
 */
export const submitAssignment = async (
	workspaceId: string,
	data: AssignmentSubmissionRequest,
): Promise<void> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/permissions/roles`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		},
	);
	if (!response.ok) {
		throw new Error('Failed to submit assignment');
	}
};

/**
 * Deletes principal role assignments to the workspace permissions API.
 * DELETE /api/studio/workspace/permissions/roles
 */
export const deleteAssignment = async (
	workspaceId: string,
	data: AssignmentDeletionRequest,
): Promise<void> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/permissions/roles`,
		{
			method: 'DELETE',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		},
	);
	if (!response.ok) {
		throw new Error('Failed to delete assignment');
	}
};

/**
 * Assigns a role to a principal.
 * POST /api/studio/workspace/permissions/role
 */
export const assignRoleToPrincipal = async (
	workspaceId: string,
	data: RoleAssignmentRequest,
): Promise<void> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/permissions/role`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		},
	);
	if (!response.ok) {
		throw new Error('Failed to assign role to principal');
	}
};

/**
 * Deletes a role from a principal.
 * DELETE /api/studio/workspace/permissions/role
 */
export const deleteRoleFromPrincipal = async (
	workspaceId: string,
	data: RoleAssignmentRequest,
): Promise<void> => {
	const response = await fetch(
		`/gateway/studio/workspace/${workspaceId}/api/studio/workspace/permissions/role`,
		{
			method: 'DELETE',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		},
	);
	if (!response.ok) {
		throw new Error('Failed to delete role from principal');
	}
};
